
import { OpeningDay, SpecialClosure } from '../types';

export const getWorkingDayBoundaries = (date: Date, openingHours?: OpeningDay[]) => {
  const days = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
  
  const getOpeningTimeForDate = (d: Date) => {
    const dayName = days[d.getDay()];
    const config = openingHours?.find(oh => oh.dayName === dayName);
    if (config && config.status === 'open' && config.slots.length > 0) {
      const earliestSlot = config.slots.reduce((earliest, slot) => {
         const [h, m] = slot.start.split(':').map(Number);
         const slotMins = h * 60 + m;
         const [eh, em] = earliest.start.split(':').map(Number);
         const earliestMins = eh * 60 + em;
         return slotMins < earliestMins ? slot : earliest;
      }, config.slots[0]);
      const [h, m] = earliestSlot.start.split(':').map(Number);
      return h * 60 + m;
    }
    return 6 * 60; // Default to 06:00
  };

  const nowMinutes = date.getHours() * 60 + date.getMinutes();
  const openingMinutesToday = getOpeningTimeForDate(date);

  // If we haven't reached today's opening time yet, we are still conceptually in "yesterday's" shift,
  // but only if we are still in the overnight hours (before 06:00 AM) or yesterday actually had an overnight slot that hasn't ended yet.
  const logicalDate = new Date(date);
  if (nowMinutes < openingMinutesToday) {
     let shouldShiftBack = false;
     if (date.getHours() < 6) {
        shouldShiftBack = true;
     } else {
        const yesterdayDate = new Date(date);
        yesterdayDate.setDate(yesterdayDate.getDate() - 1);
        const yesterdayDayName = days[yesterdayDate.getDay()];
        const yesterdayConfig = openingHours?.find(oh => oh.dayName === yesterdayDayName);
        if (yesterdayConfig && yesterdayConfig.status === 'open') {
           const hasActiveOvernight = yesterdayConfig.slots.some(slot => {
              const [sH, sM] = slot.start.split(':').map(Number);
              const [eH, eM] = slot.end.split(':').map(Number);
              const startSlot = sH * 60 + sM;
              const endSlot = eH * 60 + eM;
              return (endSlot < startSlot && nowMinutes < endSlot);
           });
           if (hasActiveOvernight) {
              shouldShiftBack = true;
           }
        }
     }
     if (shouldShiftBack) {
        logicalDate.setDate(logicalDate.getDate() - 1);
     }
  }

  // The start of the logical day is its opening time.
  const start = new Date(logicalDate);
  const startOpeningMinutes = getOpeningTimeForDate(start);
  start.setHours(Math.floor(startOpeningMinutes / 60), startOpeningMinutes % 60, 0, 0);

  // The end of the logical day is the next day's opening time.
  const end = new Date(logicalDate);
  end.setDate(end.getDate() + 1);
  const endOpeningMinutes = getOpeningTimeForDate(end);
  end.setHours(Math.floor(endOpeningMinutes / 60), endOpeningMinutes % 60, 0, 0);

  return { start: start.getTime(), end: end.getTime() };
};

export const isShopOpen = (openingHours: OpeningDay[], specialClosures: SpecialClosure[] = []) => {
    const now = new Date();
    const logicalNow = new Date(now);
    if (logicalNow.getHours() < 6) logicalNow.setDate(logicalNow.getDate() - 1);
    const todayStr = logicalNow.toLocaleDateString('en-CA'); 
    const specialClosure = specialClosures?.find(c => c.date === todayStr);
    
    if (specialClosure) return false;

    if (!openingHours || openingHours.length === 0) return true; 
    
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const days = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
    const todayName = days[now.getDay()];
    const yesterdayName = days[(now.getDay() + 6) % 7];

    const check = (dayConfig: OpeningDay | undefined, overnight = false) => {
        if (!dayConfig || dayConfig.status === 'closed') return false;
        return dayConfig.slots.some(slot => {
            const [sH, sM] = slot.start.split(':').map(Number);
            const [eH, eM] = slot.end.split(':').map(Number);
            const start = sH * 60 + sM;
            const end = eH * 60 + eM;
            if (overnight) return end < start ? currentMinutes < end : false;
            return end < start ? currentMinutes >= start : (currentMinutes >= start && currentMinutes < end);
        });
    };

    if (check(openingHours.find(d => d.dayName === todayName), false)) return true;
    if (check(openingHours.find(d => d.dayName === yesterdayName), true)) return true;
    
    return false;
};
